import type { ReactNode } from 'react';

/**
 * v3.1 §5 Bausteine — Klappzeile primitive.
 *
 * Verbatim class: `flex items-center gap-3 border-t border-b
 * border-dream-navy/10 py-4 text-sm`. Arrow `▸` (closed) / `▾` (open)
 * on the left in `text-dream-navy/70`. Ändern-Link on the right as
 * `text-sm underline` in the beantwortet state (rendered by the caller
 * via `trailing`).
 *
 * "Neubau. Kein Akkordeon in der PWA" (v3.1 §5 Bausteine table).
 *
 * ═══ THREE STATES ═════════════════════════════════════════════════════
 *
 *   'closed'     — ▸ + label + optional trailing (e.g. Bestandsangabe)
 *   'open'       — ▾ + label + optional trailing
 *   'answered'   — ▸ + label + trailing (answer + "Ändern" link)
 *
 * ═══ WHERE THIS IS USED ═══════════════════════════════════════════════
 *
 * v3.1 §6 Schritt 1 line 263 — Anlass-Klappzeile, three states
 * (zu / offen / beantwortet). "Die erfundene Geschichte liegt im
 * geöffneten Block, S-5."
 *
 * v3.1 §6 Schritt 3 lines 312-313 — two collapse levels:
 *   `▸ Seite für Seite lesen und ändern` · Bestandsangabe rechts
 *   `▸ Dinge, die im Buch vorkommen` · Bestandsangabe rechts
 * "Kapitel gibt es nicht" (D-28).
 *
 * ═══ SEMANTICS ═══════════════════════════════════════════════════════
 *
 * Native `<button>` with `aria-expanded` for closed / open transitions
 * so keyboard + screen readers get the disclosure pattern for free.
 * The `answered` state re-uses `aria-expanded="false"` (the block IS
 * closed with an answer shown) plus visible answer content on the
 * right — a click still expands (`onToggle`).
 *
 * The Ändern-Link in the answered trailing is a nested interactive
 * element. Native <button> nesting is invalid HTML, so the caller
 * renders the Ändern link as a `<button type="button">` inside
 * trailing AND passes `stopPropagation` so the row's `onToggle` does
 * not fire when the Ändern link is clicked. This is a caller
 * responsibility; the primitive does not intercept.
 */

export type KlappzeileState = 'closed' | 'open' | 'answered';

export interface KlappzeileProps {
  readonly state: KlappzeileState;
  readonly label: string;
  /** Right-side content — Bestandsangabe count, or answer + Ändern link. */
  readonly trailing?: ReactNode;
  /** Fires on any click of the row (not on nested interactive elements). */
  readonly onToggle: () => void;
  /** id of the expandable panel this row controls, for aria-controls. */
  readonly ariaControls?: string;
}

const ARROW: Record<KlappzeileState, string> = {
  closed: '▸',
  open: '▾',
  answered: '▸',
};

export function Klappzeile({ state, label, trailing, onToggle, ariaControls }: KlappzeileProps) {
  const expanded = state === 'open';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={ariaControls}
      className="flex w-full items-center gap-3 border-t border-b border-dream-navy/10 py-4 text-sm text-dream-navy"
    >
      <span aria-hidden className="text-dream-navy/70">
        {ARROW[state]}
      </span>
      <span className="flex-1 text-left">{label}</span>
      {trailing !== undefined && <span className="text-dream-navy/70">{trailing}</span>}
    </button>
  );
}
